import { indexTargets, resolveTargetForCell } from "@/grid/data/resolve-target";
import {
  monthStartsInQuarter,
  monthStart,
  resolvePeriodKeys,
  toIsoDate,
  nowToronto,
} from "@/grid/core/periods";
import { indexMetricConfigs } from "@/grid/core/target-policy";
import type {
  DailyActivityRow,
  DepartmentDefinition,
  GridCell,
  GridRawData,
  GridTeam,
  GridViewModel,
  MetricDefinition,
  PeriodId,
  Region,
  RepPeriodRow,
  TeamPeriodRow,
  TeamRow,
} from "@/grid/core/types";

export interface TeamGroup {
  id: number;
  name: string;
  groupKey: string;
  teamIds: number[];
  region: Region | "COMBINED";
}

function sumDailyField(
  repIds: number[],
  rows: DailyActivityRow[],
  from: string,
  to: string,
  field: keyof Pick<
    DailyActivityRow,
    "total_calls" | "connected_calls" | "meetings_held" | "sequences"
  >,
): number | null {
  let total = 0;
  let hasValue = false;

  for (const row of rows) {
    if (!repIds.includes(row.rep_id)) continue;
    if (row.activity_date < from || row.activity_date > to) continue;
    const value = row[field];
    if (value == null) continue;
    total += value;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function findRepPeriodRow(
  rows: RepPeriodRow[],
  repIds: number[],
  periodType: string,
  periodStart: string,
): RepPeriodRow | undefined {
  return rows.find(
    (row) =>
      repIds.includes(row.rep_id) &&
      row.period_type === periodType &&
      row.period_start === periodStart,
  );
}

function readRepPeriodField(row: RepPeriodRow | undefined, field: "sqls"): number | null {
  if (!row) return null;
  return row[field];
}

function sumRepPeriodWeeksFromMonthStart(
  rows: RepPeriodRow[],
  repIds: number[],
  fromDate: string,
  throughDate: string,
  field: "sqls",
): number | null {
  let total = 0;
  let hasValue = false;

  for (const row of rows) {
    if (!repIds.includes(row.rep_id)) continue;
    if (row.period_type !== "week") continue;
    // Week belongs to the month of its Monday. Do not pull in the prior
    // month's straddling week (Aug 2026 MTD was including July 27-31).
    if (row.period_start < fromDate) continue;
    if (row.period_start > throughDate) continue;
    const value = row[field];
    if (value == null) continue;
    total += value;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function findTeamPeriodRow(
  rows: TeamPeriodRow[],
  teamId: number,
  periodType: string,
  periodStart: string,
): TeamPeriodRow | undefined {
  return rows.find(
    (row) =>
      row.team_id === teamId && row.period_type === periodType && row.period_start === periodStart,
  );
}

function readPeriodField(
  row: TeamPeriodRow | undefined,
  field: "sales_amount" | "opps_generated_count" | "opps_generated_value",
): number | null {
  if (!row) return null;
  return row[field];
}

function sumPeriodFieldForTeams(
  rows: TeamPeriodRow[],
  teamIds: number[],
  periodType: string,
  periodStart: string,
  field: "sales_amount" | "opps_generated_count" | "opps_generated_value",
): number | null {
  let total = 0;
  let hasValue = false;

  for (const teamId of teamIds) {
    const row = findTeamPeriodRow(rows, teamId, periodType, periodStart);
    const value = readPeriodField(row, field);
    if (value == null) continue;
    total += value;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function sumQuarterFieldForTeams(
  rows: TeamPeriodRow[],
  teamIds: number[],
  field: "sales_amount" | "opps_generated_count" | "opps_generated_value",
  asOf = nowToronto(),
): number | null {
  const monthStarts = monthStartsInQuarter(asOf);
  let total = 0;
  let hasValue = false;

  for (const monthStart of monthStarts) {
    for (const teamId of teamIds) {
      const row = findTeamPeriodRow(rows, teamId, "month", monthStart);
      const value = readPeriodField(row, field);
      if (value == null) continue;
      total += value;
      hasValue = true;
    }
  }

  return hasValue ? total : null;
}

export function resolveCellValue(
  metric: MetricDefinition,
  periodId: PeriodId,
  teamIds: number[],
  repIds: number[],
  raw: GridRawData,
  asOf = nowToronto(),
): number | null {
  const keys = resolvePeriodKeys(periodId, asOf);

  if (periodId === "mtd" && metric.source.kind === "rep-period") {
    return sumRepPeriodWeeksFromMonthStart(
      raw.repPeriodMetrics,
      repIds,
      monthStart(asOf),
      toIsoDate(asOf),
      metric.source.field,
    );
  }

  if (periodId === "qtd" && metric.source.kind === "team-period") {
    return sumQuarterFieldForTeams(raw.teamPeriodMetrics, teamIds, metric.source.field, asOf);
  }

  switch (metric.source.kind) {
    case "daily-sum": {
      if (!keys.dailyFrom || !keys.dailyTo) return null;
      return sumDailyField(
        repIds,
        raw.dailyActivities,
        keys.dailyFrom,
        keys.dailyTo,
        metric.source.field,
      );
    }
    case "team-period": {
      if (!keys.periodType || !keys.periodStart) return null;
      return sumPeriodFieldForTeams(
        raw.teamPeriodMetrics,
        teamIds,
        keys.periodType,
        keys.periodStart,
        metric.source.field,
      );
    }
    case "rep-period": {
      if (!keys.periodType || !keys.periodStart) return null;
      const row = findRepPeriodRow(raw.repPeriodMetrics, repIds, keys.periodType, keys.periodStart);
      return readRepPeriodField(row, metric.source.field);
    }
    case "rep-period-month-sum":
      return sumRepPeriodWeeksFromMonthStart(
        raw.repPeriodMetrics,
        repIds,
        monthStart(asOf),
        toIsoDate(asOf),
        metric.source.field,
      );
    case "team-quarter-sum":
      return sumQuarterFieldForTeams(raw.teamPeriodMetrics, teamIds, metric.source.field, asOf);
  }

  return null;
}

function toRegion(value: string): Region {
  return value === "US" ? "US" : "CAN";
}

function sortTeamRows(a: TeamRow, b: TeamRow): number {
  if (a.region !== b.region) {
    if (a.region === "CAN") return -1;
    if (b.region === "CAN") return 1;
  }
  return a.name.localeCompare(b.name);
}

function groupKey(team: TeamRow): string {
  return team.syncore_ref ?? `id:${team.id}`;
}

export function buildTeamGroups(teams: TeamRow[], department: DepartmentDefinition): TeamGroup[] {
  const filtered = teams
    .filter((team) => team.department === department.id)
    .filter((team) => !department.regions || department.regions.includes(toRegion(team.region)));

  if (!department.combineRegions) {
    return filtered.sort(sortTeamRows).map((team) => ({
      id: team.id,
      name: team.name,
      groupKey: groupKey(team),
      teamIds: [team.id],
      region: toRegion(team.region),
    }));
  }

  const grouped = new Map<string, TeamRow[]>();
  for (const team of filtered) {
    const key = groupKey(team);
    const members = grouped.get(key) ?? [];
    members.push(team);
    grouped.set(key, members);
  }

  return Array.from(grouped.values())
    .map((members) => {
      const sorted = [...members].sort(sortTeamRows);
      const canonical = sorted[0]!;
      const regions = [...new Set(sorted.map((team) => toRegion(team.region)))];
      return {
        id: canonical.id,
        name: canonical.name,
        groupKey: groupKey(canonical),
        teamIds: sorted.map((team) => team.id),
        region: regions.length > 1 ? ("COMBINED" as const) : regions[0]!,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function assembleGrid(
  department: DepartmentDefinition,
  raw: GridRawData,
  asOf = nowToronto(),
): GridViewModel {
  const repsByTeam = new Map<number, number[]>();
  for (const rep of raw.reps) {
    const list = repsByTeam.get(rep.team_id) ?? [];
    list.push(rep.id);
    repsByTeam.set(rep.team_id, list);
  }

  const targetIndex = indexTargets(raw.targets);
  const configByMetric = indexMetricConfigs(
    department.id,
    department.metrics,
    raw.targetMetricConfigs,
  );

  const teams: GridTeam[] = buildTeamGroups(raw.teams, department).map((group) => {
    const repIds = group.teamIds.flatMap((teamId) => repsByTeam.get(teamId) ?? []);
    const cells: GridCell[] = [];
    const scope = {
      departmentId: department.id,
      teamGroupKey: group.groupKey,
      teamId: group.id,
    };

    for (const metric of department.metrics) {
      for (const period of department.periods) {
        const actual = resolveCellValue(metric, period.id, group.teamIds, repIds, raw, asOf);
        const resolved = resolveTargetForCell(
          metric,
          period.id,
          scope,
          targetIndex,
          configByMetric,
          asOf,
        );
        cells.push({
          metricId: metric.id,
          periodId: period.id,
          actual,
          target: resolved.target,
          pacingTarget: resolved.pacingTarget,
          targetSource: resolved.targetSource,
        });
      }
    }

    return {
      id: group.id,
      name: group.name,
      region: group.region,
      department: department.id,
      cells,
    };
  });

  return {
    department,
    teams,
    asOf: toIsoDate(asOf),
    fetchedAt: new Date().toISOString(),
  };
}

export function getCell(
  team: GridTeam,
  metricId: string,
  periodId: PeriodId,
): GridCell | undefined {
  return team.cells.find((cell) => cell.metricId === metricId && cell.periodId === periodId);
}
